import { useState } from 'react';
import { CheckCircle, X, Zap } from 'lucide-react';
import { sendRadianEvent, RADIAN_EVENTS } from '../../../usecases/radian-usecase';
import { dispatchCustomEvent } from '../../../utils/dispatch';
import './style.css';

const REJECTION_TYPES = [
  { id: 1, label: 'Factura con error en datos del receptor' },
  { id: 2, label: 'Factura con error en datos del emisor' },
  { id: 3, label: 'Bienes o servicios no recibidos' },
  { id: 4, label: 'Valor no corresponde a lo pactado' },
];

/**
 * RadianEventsModal
 *
 * Props:
 *  - xmlInvoiceData: { cufe, invoice_number, supplier_nit, supplier_name, supplier_email, sent_events }
 *  - onClose: () => void
 */
const RadianEventsModal = ({ xmlInvoiceData, onClose }) => {
  const [sentEvents, setSentEvents] = useState(() => {
    if (!xmlInvoiceData) return {};
    let dataObj = xmlInvoiceData;
    if (typeof xmlInvoiceData === 'string') {
      try {
        dataObj = JSON.parse(xmlInvoiceData);
      } catch (e) {
        dataObj = {};
      }
    }
    const { sent_events } = dataObj || {};
    if (!sent_events) return {};
    const list = Array.isArray(sent_events) ? sent_events : Object.keys(sent_events);
    return list.reduce((acc, code) => {
      acc[code] = true;
      return acc;
    }, {});
  });
  const [loadingEvent, setLoadingEvent] = useState(null);
  const [typeRejectionId, setTypeRejectionId] = useState(1);

  if (!xmlInvoiceData) return null;

  let dataObj = xmlInvoiceData;
  if (typeof xmlInvoiceData === 'string') {
    try {
      dataObj = JSON.parse(xmlInvoiceData);
    } catch (e) {
      dataObj = {};
    }
  }

  const { cufe, invoice_number, supplier_nit, supplier_name, supplier_email } = dataObj || {};

  const handleSend = async (eventCode) => {
    if (!cufe || !invoice_number || !supplier_nit) {
      dispatchCustomEvent('popup', {
        message: 'Faltan datos de la factura XML (CUFE, número o NIT del proveedor).',
        type: 'warning',
      });
      return;
    }

    setLoadingEvent(eventCode);
    try {
      await sendRadianEvent(
        eventCode,
        cufe,
        invoice_number,
        supplier_nit,
        supplier_name || '',
        supplier_email,
        eventCode === '034' ? typeRejectionId : undefined
      );

      setSentEvents((prev) => ({ ...prev, [eventCode]: true }));
      dispatchCustomEvent('popup', {
        message: `Evento RADIAN ${RADIAN_EVENTS[eventCode]?.name || eventCode} enviado correctamente.`,
        type: 'success',
      });
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        err?.response?.data?.technical_error ||
        err?.message ||
        `Error al enviar el evento ${eventCode}`;
      dispatchCustomEvent('popup', { message: msg, type: 'error' });
    } finally {
      setLoadingEvent(null);
    }
  };

  const allEvents = Object.values(RADIAN_EVENTS);
  const nonRejectionEvents = allEvents.filter((e) => e.code !== '031');
  const rejectionEvent = RADIAN_EVENTS['031'];

  const renderEventCard = (event) => {
    if (!event) return null;
    const isSent = !!sentEvents[event.code];
    const isLoading = loadingEvent === event.code;
    const isBlocked = loadingEvent !== null && !isLoading;
    const hasRejected = !!sentEvents['031'];
    const isConflict =
      (event.code === '033' && hasRejected) ||
      (event.code === '031' && sentEvents['033']);

    return (
      <div key={event.code} className={`radian-event-card${isSent ? ' sent' : ''}`}>
        {/* Badge */}
        <div
          className="radian-event-badge"
          style={{
            background: `${event.color}20`,
            color: event.color,
            border: `1.5px solid ${event.color}40`,
          }}
        >
          {event.code}
        </div>

        {/* Info */}
        <div className="radian-event-info">
          <div className="radian-event-name">{event.name}</div>
          <div className="radian-event-desc">{event.description}</div>
        </div>

        {/* Action */}
        <div className="radian-event-action">
          {isSent ? (
            <span className="radian-sent-badge">
              <CheckCircle size={14} />
              Enviado
            </span>
          ) : (
            <button
              className="radian-send-btn"
              style={{ background: event.color, color: '#fff' }}
              disabled={isBlocked || isConflict}
              onClick={() => handleSend(event.code)}
              title={isConflict ? 'No compatible con el evento ya enviado' : ''}
            >
              {isLoading ? (
                <span className="radian-loading-spinner" />
              ) : (
                'Emitir'
              )}
            </button>
          )}
        </div>
      </div>
    );
  };

  const cufeShort = cufe ? `${cufe.slice(0, 20)}...${cufe.slice(-8)}` : 'No disponible';

  return (
    <div className="radian-modal-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="radian-modal">
        <button className="radian-close-btn" onClick={onClose} title="Cerrar">
          <X size={18} />
        </button>
        {/* Header */}
        <div className="radian-modal-header">
          <div>
            <h2 className="radian-modal-title">
              <Zap size={16} style={{ marginRight: 6, verticalAlign: 'middle', color: '#f59e0b' }} />
              Eventos RADIAN - DIAN
            </h2>
            <div className="radian-invoice-meta">
              <span><strong>Factura:</strong> {invoice_number || '—'}</span>
              <span><strong>Proveedor:</strong> {supplier_name || '—'} ({supplier_nit || '—'})</span>
              <span className="cufe-badge" title={cufe}>CUFE: {cufeShort}</span>
            </div>
          </div>
        </div>

        <hr className="radian-divider" />

        {/* Non-rejection events group */}
        <div className="radian-events-grid">
          {nonRejectionEvents.map(renderEventCard)}
        </div>

        {/* Rejection section (Evento 031 + Selector) */}
        {rejectionEvent && (
          <div className="radian-rejection-section">
            {renderEventCard(rejectionEvent)}

            {!sentEvents['031'] && !sentEvents['034'] && (
              <div className="radian-rejection-selector-wrapper">
                <div className="radian-rejection-label">
                  Motivo de rechazo / reclamo (se aplicará al emitir evento 031)
                </div>
                <select
                  className="radian-rejection-select"
                  value={typeRejectionId}
                  onChange={(e) => setTypeRejectionId(Number(e.target.value))}
                >
                  {REJECTION_TYPES.map((r) => (
                    <option key={r.id} value={r.id}>
                      {r.id}. {r.label}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};




export default RadianEventsModal;
